import type {
  CargoPayload,
  ContainerPayload,
  IdentityPayload,
  LoadType,
  ShipmentContainer,
  ShipmentDetail,
  VolumeUnit,
  WeightUnit,
} from "api";

/**
 * The Cargo tab's form. Every field is the string the input holds; `toCargoForm`
 * and the two payload builders translate to and from the API's nulls and
 * numbers. Rows carry a `key` of their own so React and the error map can
 * follow a row across adds, removes and a save that renumbers them.
 */
export type CargoLineForm = {
  key: string;
  name: string;
  package_type: string;
  package_quantity: string;
  gross_weight: string;
  weight_unit: WeightUnit;
  volume: string;
  volume_unit: VolumeUnit;
};

export type ContainerForm = {
  key: string;
  /** Null for a row added since the last save. */
  id: number | null;
  number: string;
  container_type: string;
  /** Empty means "Not dangerous". */
  dg_class: string;
  cargo_group: string;
  gross_weight_mt: string;
  note: string;
  cargo_lines: CargoLineForm[];
};

export type IdentityForm = {
  load_type: LoadType;
  booking_number: string;
  master_bl: string;
  house_bl: string;
  item_name: string;
  item_description: string;
};

export type CargoForm = IdentityForm & { containers: ContainerForm[] };

export const IDENTITY_FIELDS = [
  "load_type",
  "booking_number",
  "master_bl",
  "house_bl",
  "item_name",
  "item_description",
] as const;

/* ---- the design's option lists (design/account/account.js) ---- */

export type DgOption = { value: string; label: string; hint: string; group: string };

const DG_GROUPS: [string, [string, string][]][] = [
  ["", [["", "No dangerous goods"]]],
  [
    "Class 1 — Explosives",
    [
      ["1.1", "Mass explosion hazard"],
      ["1.2", "Projection hazard, no mass explosion"],
      ["1.3", "Fire hazard, minor blast hazard"],
      ["1.4", "Minor hazard"],
      ["1.5", "Very insensitive, mass explosion hazard"],
      ["1.6", "Extremely insensitive, no mass explosion"],
    ],
  ],
  [
    "Class 2 — Gases",
    [
      ["2.1", "Flammable gases"],
      ["2.2", "Non-flammable, non-toxic gases"],
      ["2.3", "Toxic gases"],
    ],
  ],
  ["Class 3 — Flammable liquids", [["3", "Flammable liquids"]]],
  [
    "Class 4 — Flammable solids",
    [
      ["4.1", "Flammable solids, self-reactive"],
      ["4.2", "Spontaneously combustible"],
      ["4.3", "Dangerous when wet"],
    ],
  ],
  [
    "Class 5 — Oxidizers and peroxides",
    [
      ["5.1", "Oxidizing substances"],
      ["5.2", "Organic peroxides"],
    ],
  ],
  [
    "Class 6 — Toxic and infectious",
    [
      ["6.1", "Toxic substances"],
      ["6.2", "Infectious substances"],
    ],
  ],
  [
    "Class 7 — Radioactive material",
    [
      ["7 I-WHITE", "Category I-WHITE"],
      ["7 II-YELLOW", "Category II-YELLOW"],
      ["7 III-YELLOW", "Category III-YELLOW"],
    ],
  ],
  ["Class 8 — Corrosive substances", [["8", "Corrosive substances"]]],
  ["Class 9 — Miscellaneous", [["9", "Miscellaneous dangerous goods"]]],
];

export const NOT_DANGEROUS = "Not dangerous";

export const DG_OPTIONS: DgOption[] = DG_GROUPS.flatMap(([group, options]) =>
  options.map(([value, hint]) => ({ value, label: value || NOT_DANGEROUS, hint, group }))
);

/** Compatibility groups for class 1, as the design lists them. */
export const CARGO_GROUPS = "ABCDEFGHJKLNS".split("");

export const CONTAINER_TYPES = [
  "20' Standard",
  "40' Standard",
  "40' High Cube",
  "45' High Cube",
  "20' Reefer",
  "40' Reefer",
  "20' Open Top",
  "40' Flat Rack",
  "Truck",
  "Wagon",
  "Air ULD",
];

export const PACKAGE_TYPES = [
  "Pallet",
  "Carton / Box",
  "Crate",
  "Wooden Case",
  "Bundle",
  "Bag / Sack",
  "Drum",
  "Barrel",
  "Roll",
  "Bale",
  "IBC Tank",
  "Loose Cargo",
];

export const WEIGHT_UNITS: WeightUnit[] = ["mt", "kg", "lb"];

export const VOLUME_UNITS: VolumeUnit[] = ["CBM", "CFT"];

/** A new row starts on the type of the row above it, or the design's default. */
export const DEFAULT_CONTAINER_TYPE = "40' High Cube";

export const DEFAULT_PACKAGE_TYPE = "Pallet";

/** The container note's limit in the design's drawer (the API allows more). */
export const NOTE_MAX_LENGTH = 150;

/* ---- rules ---- */

/** Only class 1 carries a compatibility group; the picker shows beside it then. */
export const hasCargoGroup = (dgClass: string): boolean => /^1\./.test(dgClass);

/**
 * The design's "gross weight: digits only": digits and one point, no leading
 * zeros, at most three decimals — the API stores tonnes to the kilogram.
 */
export const sanitizeDecimal = (raw: string, decimals = 3): string => {
  const [whole = "", ...rest] = raw.replace(/[^0-9.]/g, "").split(".");
  const integer = whole.replace(/^0+(?=\d)/, "");

  return rest.length === 0 ? integer : `${integer}.${rest.join("").slice(0, decimals)}`;
};

export const sanitizeInteger = (raw: string): string => raw.replace(/\D/g, "").replace(/^0+(?=\d)/, "");

/* ---- API ⇄ form ---- */

let keySeed = 0;

/** Row keys only need to be unique within the page's lifetime. */
export const nextKey = (): string => {
  keySeed += 1;

  return `row-${keySeed}`;
};

export const blankCargoLine = (): CargoLineForm => ({
  key: nextKey(),
  name: "",
  package_type: DEFAULT_PACKAGE_TYPE,
  package_quantity: "",
  gross_weight: "",
  weight_unit: "mt",
  volume: "",
  volume_unit: "CBM",
});

export const blankContainer = (containerType = DEFAULT_CONTAINER_TYPE): ContainerForm => ({
  key: nextKey(),
  id: null,
  number: "",
  container_type: containerType,
  dg_class: "",
  cargo_group: "",
  gross_weight_mt: "",
  note: "",
  cargo_lines: [blankCargoLine()],
});

const text = (value: string | number | null): string => (value === null ? "" : String(value));

/**
 * The API's decimals come fixed-scale ("2400.000"); the grid's number cells
 * are narrow, so the form shows what was typed ("2400").
 */
const decimal = (value: string | null): string => {
  if (value === null) return "";

  return value.includes(".") ? value.replace(/\.?0+$/, "") : value;
};

const toContainerForm = (container: ShipmentContainer): ContainerForm => ({
  key: nextKey(),
  id: container.id,
  number: text(container.number),
  container_type: text(container.container_type),
  dg_class: text(container.dg_class),
  cargo_group: text(container.cargo_group),
  gross_weight_mt: decimal(container.gross_weight_mt),
  note: text(container.note),
  // An LCL row always shows one line to type into, even before it has one.
  cargo_lines:
    container.cargo_lines.length === 0
      ? [blankCargoLine()]
      : container.cargo_lines.map((line) => ({
          key: nextKey(),
          name: text(line.name),
          package_type: text(line.package_type),
          package_quantity: text(line.package_quantity),
          gross_weight: decimal(line.gross_weight),
          weight_unit: line.weight_unit,
          volume: decimal(line.volume),
          volume_unit: line.volume_unit,
        })),
});

/**
 * The form for a loaded shipment. The grid always has a row to type into,
 * as the design draws it: an empty one is dropped again when saving.
 */
export const toCargoForm = (shipment: ShipmentDetail): CargoForm => ({
  load_type: shipment.load_type ?? "FCL",
  booking_number: text(shipment.booking_number),
  master_bl: text(shipment.master_bl),
  house_bl: text(shipment.house_bl),
  item_name: text(shipment.item_name),
  item_description: text(shipment.item_description),
  containers: shipment.containers.length === 0 ? [blankContainer()] : shipment.containers.map(toContainerForm),
});

export const lineHasData = (line: CargoLineForm): boolean =>
  [line.name, line.package_quantity, line.gross_weight, line.volume].some((value) => value.trim() !== "");

/**
 * Whether the row holds anything worth a confirmation before it goes. The
 * type and the "not dangerous" default are pre-filled, so they do not count.
 */
export const containerHasData = (container: ContainerForm): boolean =>
  [container.number, container.gross_weight_mt, container.note, container.dg_class].some(
    (value) => value.trim() !== ""
  ) || container.cargo_lines.some(lineHasData);

/** Strings from the form, compared the way the payload will read them. */
export const identityChanged = (form: CargoForm, initial: CargoForm): boolean =>
  IDENTITY_FIELDS.some((field) => form[field].trim() !== initial[field].trim());

/** Keys aside, the grid the payload would send differs from the one loaded. */
export const gridChanged = (form: CargoForm, initial: CargoForm): boolean =>
  JSON.stringify(toCargoPayload(form).payload) !== JSON.stringify(toCargoPayload(initial).payload);

const orNull = (value: string): string | null => (value.trim() === "" ? null : value.trim());

const numberOrNull = (value: string): number | null => (value.trim() === "" ? null : Number(value));

export const toIdentityPayload = (form: CargoForm): IdentityPayload => ({
  load_type: form.load_type,
  booking_number: orNull(form.booking_number),
  master_bl: orNull(form.master_bl),
  house_bl: orNull(form.house_bl),
  item_name: orNull(form.item_name),
  item_description: orNull(form.item_description),
});

/**
 * Which form row each payload index came from, so a 422's
 * `containers.1.cargo_lines.0.weight_unit` lands on the row the visitor sees.
 */
export type PayloadRows = { containers: string[]; lines: string[][] };

/**
 * The grid as `PUT …/cargo` takes it: the whole list, in order. New rows with
 * nothing typed in are left out; FCL sends no cargo lines, because the lines
 * are what LCL shares a container by. The compatibility group only exists for
 * class 1, so any other class drops it.
 */
export const toCargoPayload = (form: CargoForm): { payload: CargoPayload; rows: PayloadRows } => {
  const rows: PayloadRows = { containers: [], lines: [] };
  const containers: ContainerPayload[] = [];

  // A row the server already has is kept even when emptied: dropping it would delete it.
  form.containers
    .filter((container) => container.id !== null || containerHasData(container))
    .forEach((container) => {
      const lines = form.load_type === "LCL" ? container.cargo_lines.filter(lineHasData) : [];

      rows.containers.push(container.key);
      rows.lines.push(lines.map((line) => line.key));
      containers.push({
        number: orNull(container.number),
        container_type: orNull(container.container_type),
        dg_class: orNull(container.dg_class),
        cargo_group: hasCargoGroup(container.dg_class) ? orNull(container.cargo_group) : null,
        gross_weight_mt: numberOrNull(container.gross_weight_mt),
        note: orNull(container.note),
        cargo_lines: lines.map((line) => ({
          name: orNull(line.name),
          package_type: orNull(line.package_type),
          package_quantity: numberOrNull(line.package_quantity),
          gross_weight: numberOrNull(line.gross_weight),
          weight_unit: line.weight_unit,
          volume: numberOrNull(line.volume),
          volume_unit: line.volume_unit,
        })),
      });
    });

  return { payload: { containers }, rows };
};

/**
 * A 422's `errors` bag, re-keyed onto the form: identity fields keep their
 * name, a grid field becomes `<row key>.<field>`. Anything that does not map
 * to a field on screen is returned under `""`, for the note above the form.
 */
/**
 * How a grid field reads in a sentence. The back office names no attributes,
 * so its messages say "The containers.1.number field…"; the path is swapped
 * for these.
 */
const GRID_FIELD_NAMES: Record<string, string> = {
  number: "container number",
  container_type: "container type",
  dg_class: "DG class",
  cargo_group: "compatibility group",
  gross_weight_mt: "gross weight",
  note: "note",
  name: "cargo name",
  package_type: "package type",
  package_quantity: "package quantity",
  gross_weight: "gross weight",
  weight_unit: "weight unit",
  volume: "volume",
  volume_unit: "volume unit",
};

export const mapFieldErrors = (errors: Record<string, string[]>, rows?: PayloadRows): Record<string, string> => {
  const mapped: Record<string, string> = {};

  Object.entries(errors).forEach(([path, messages]) => {
    const line = /^containers\.(\d+)\.cargo_lines\.(\d+)\.(\w+)$/.exec(path);
    const container = /^containers\.(\d+)\.(\w+)$/.exec(path);
    const field = line?.[3] ?? container?.[2];
    const name = field === undefined ? undefined : GRID_FIELD_NAMES[field];
    const message = name === undefined ? (messages[0] ?? "") : (messages[0] ?? "").replace(path, name);
    let key: string | undefined;

    if (line !== null) {
      const lineKey = rows?.lines[Number(line[1])]?.[Number(line[2])];

      key = lineKey === undefined ? undefined : `${lineKey}.${line[3]}`;
    } else if (container !== null) {
      const containerKey = rows?.containers[Number(container[1])];

      key = containerKey === undefined ? undefined : `${containerKey}.${container[2]}`;
    } else if ((IDENTITY_FIELDS as readonly string[]).includes(path)) {
      key = path;
    }

    const target = key ?? "";

    mapped[target] = mapped[target] ?? message;
  });

  return mapped;
};

/** "· 2" in FCL, "· 3 cargo lines" in LCL — the count beside the grid's heading. */
export const gridCount = (form: CargoForm): string => {
  if (form.load_type === "FCL") return `· ${form.containers.length}`;

  const lines = form.containers.reduce((sum, container) => sum + container.cargo_lines.length, 0);

  return `· ${lines} ${lines === 1 ? "cargo line" : "cargo lines"}`;
};

/** "Master Bill of Lading", or the air equivalent — the design's label for the B/L field. */
export const billLabel = (shipment: ShipmentDetail): { label: string; placeholder: string } =>
  shipment.shipment_type === "AIR"
    ? { label: "Air Waybill", placeholder: "e.g. 020-77219845" }
    : { label: "Master Bill of Lading", placeholder: "e.g. MAEU2938471" };

/** An option list that also carries a stored value the design's list does not name. */
export const withCurrent = (options: string[], current: string): string[] =>
  current === "" || options.includes(current) ? options : [current, ...options];
